import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import './AdminEditPost.css';
import { Circles } from 'react-loader-spinner';
import privateAxios from '../../utils/axios/privateAxios';
import publicAxios from '../../utils/axios/publicAxios';
import Notification from '../../components/Notification/Notification';
import { formats, modules, postCategories } from '../../assets/exports';
import { jwtDecode } from 'jwt-decode';

const AdminEditPost = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [decodedData, setDecodedData] = useState({});
  const userRoles = decodedData?.userRoles || [];

  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [category, setCategory] = useState('');
  const [imageUrl, setImageUrl] = useState('');
  const [originalImageUrl, setOriginalImageUrl] = useState('');
  const [imageFile, setImageFile] = useState(null);
  const [display, setDisplay] = useState(false);
  const [loading, setLoading] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [notification, setNotification] = useState({ type: '', message: '' });

  // ✅ Notification handler
  const showNotification = (type, message) => {
    setNotification({ type, message });
    setTimeout(() => setNotification({ type: '', message: '' }), 5000);
  };

  // ✅ Decode user data
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      try {
        const decoded = jwtDecode(token);
        setDecodedData(decoded);
      } catch (err) {
        navigate('/login');
      }
    }
  }, [navigate]);

  // ✅ Fetch post data
  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const res = await privateAxios.get(`/private/admin-post/${id}`);
        const { title, content, imageUrl, display, category } = res.data.post;
        setTitle(title);
        setContent(content);
        setImageUrl(imageUrl);
        setOriginalImageUrl(imageUrl);
        setDisplay(display);
        setCategory(category || userRoles[0] || '');
      } catch (error) {
        showNotification('error', 'Failed to load post.');
      } finally {
        setLoading(false);
      }
    })();
  }, [id, userRoles]);

  // ✅ Handle featured image change
  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setImageFile(file);
    setImageUrl(URL.createObjectURL(file));
  };

  // ✅ Upload standalone (featured) image to S3
  const uploadImageToS3 = async () => {
    if (!imageFile) return originalImageUrl;
    const res = await privateAxios.get('/private/generate-upload-url', {
      params: { filename: imageFile.name, filetype: imageFile.type },
    });
    await publicAxios.put(res.data.url, imageFile);
    return res.data.url.split('?')[0];
  };

  // ✅ Convert base64 -> File
  const dataURLtoFile = (dataURL, filename) => {
    const arr = dataURL.split(','), mime = arr[0].match(/:(.*?);/)[1];
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);
    while (n--) u8arr[n] = bstr.charCodeAt(n);
    return new File([u8arr], filename, { type: mime });
  };

  // ✅ Upload images embedded inside the Quill editor to S3
  const processQuillImages = async (htmlContent) => {
    const div = document.createElement('div');
    div.innerHTML = htmlContent;
    const images = div.querySelectorAll('img');
    for (let img of images) {
      if (img.src.startsWith('data:')) {
        const file = dataURLtoFile(img.src, `post-image-${Date.now()}.png`);
        const res = await privateAxios.get('/private/generate-upload-url', {
          params: { filename: file.name, filetype: file.type },
        });
        await publicAxios.put(res.data.url, file);
        img.src = res.data.url.split('?')[0];
      }
    }
    return div.innerHTML;
  };

  // ✅ Submit post update
  const handleUpdate = async (e) => {
    e.preventDefault();
    setUpdating(true);
    try {
      const updatedContent = await processQuillImages(content);
      const updatedImageUrl = imageFile ? await uploadImageToS3() : originalImageUrl;

      await privateAxios.patch(`/private/admin-edit-post/${id}`, {
        title,
        content: updatedContent,
        category,
        imageUrl: updatedImageUrl,
        display,
      });

      showNotification('success', 'Post updated successfully!');
      setTimeout(() => navigate('/admin-posts'), 3000);
    } catch (error) {
      showNotification('error', 'Update failed.');
    } finally {
      setUpdating(false);
    }
  };

  const availableCategories = userRoles.includes('admin') ? postCategories : userRoles;

  return (
    <div className="admin-edit-post">
      <Notification type={notification.type} message={notification.message} />
      <Link to="/admin-posts" className="edit-post-back">Back to Posts</Link>
      <h2>Edit Post</h2>

      {loading ? (
        <div className="admin-edit-post-loading-container">
          <Circles height="50" width="50" />
          <h2>Loading Post...</h2>
        </div>
      ) : (
        <form onSubmit={handleUpdate} className="edit-form">
          <label>Title:</label>
          <input className='admin-edit-post-title' value={title} onChange={(e) => setTitle(e.target.value)} required />

          <label>Content:</label>

          <ReactQuill
            value={content}
            onChange={setContent}
            modules={modules}
            formats={formats}
            theme="snow"
            className="quill-editor"
          />

          <label>Category:</label>
          <select
            className="edit-post-select-category"
            value={category}
            onChange={(e) => setCategory(e.target.value)}
          >
            {availableCategories.map((role) => (
              <option key={role} value={role}>
                {role.charAt(0).toUpperCase() + role.slice(1)}
              </option>
            ))}
          </select>

          {/* ✅ Add back Display Publicly */}
          {userRoles.includes('admin') && (
            <div className="display-checkbox">
              <label>
                <input
                  type="checkbox"
                  checked={display}
                  onChange={(e) => setDisplay(e.target.checked)}
                />
                Display publicly
              </label>
            </div>
          )}

          <label>Change Featured Image (optional):</label>
          <input type="file" onChange={handleImageChange} />
          <div className="admin-edit-post-image-div">
            {imageUrl && <img src={imageUrl} alt="Preview" className="image-preview-image" />}
          </div>

          <div className="edit-actions">
            <button type="submit" disabled={updating}>
              {updating ? 'Updating...' : 'Save Changes'}
            </button>
            <button type="button" onClick={() => navigate('/admin-posts')}>
              Cancel
            </button>
          </div>

        </form>
      )}
    </div>
  );
};

export default AdminEditPost;
